import i18next from "i18next";
import { AlertType } from "@caspeco/casper-ui-library.components.alert";
import { logDebug } from "helpers/log-helper/log-helper";
import { getWeekdayString } from "helpers/opening-hours-helper/opening-hours-helper";
import { Menu } from "types/menu";
import { LogPrefix } from "types/logging";
import { IOpeningHoursDisplayData, RelationalDay } from "types/openingHours";
import { PlaceDetails } from "types/place";
import { Section } from "types/section";
import { Icons } from "@caspeco/casper-ui-library.components.icon";

/**
 * Creates a text label showing the current or next relevant opening hours to display.
 * For ex. "Opens tomorrow at 12:00" or if currently open "13:00-17:00".
 */
const createCustomOpeningHoursLabel = (openingStatus: IOpeningHoursDisplayData): string | undefined => {
	const { atRelation, at, weekday } = openingStatus;
	if (!at?.opens) return undefined;

	let label: string | undefined;
	const translatedWeekday = getWeekdayString(weekday);

	switch (atRelation) {
		case RelationalDay.Today:
			label = at.opens;
			break;
		case RelationalDay.Tomorrow:
			label = i18next.t("time_tomorrow_at_x", { 0: at.opens });
			break;
		case RelationalDay.OtherWeekday: {
			label = `${translatedWeekday} ${at.opens}`;
			break;
		}
		default:
			label = undefined;
	}
	return label;
};

/**
 * Helps determine what information to display for the user indicating
 * wether the user can make a purchase or not, based on opening hours and pre-order.
 * It lives in a section scope but can display status for menu as well.
 *
 * Testing docs are available in Confluence,
 * where each scenario has a number matching the cases below.
 * @see https://caspeco.atlassian.net/wiki/spaces/BES/pages/3357278209/Testprotokoll+f+r+visning+av+status+och+ppettider#Underlag-f%C3%B6r-uppvisning-av-banner-i-artikellistan
 *
 * @param place The place to check for opening hours and pre-order status.
 * @param menu The menu to check for opening hours status.
 * @param section The section to check for opening hours status.
 * @param deliveryOptionType Used when checking for for grouped order alert status.
 *
 * @returns For ex. if @menu is closed and @place has preOrder enabled,
 * will return "Menu is only open for pre-order. Opens XX:XX.".
 * Returns undefined if no information is to be displayed for current "status".
 */
export const getMenuSectionAlert = (
	place: PlaceDetails | undefined,
	menu: Menu | undefined,
	section: Section | undefined,
	deliveryOptionType?: string,
): { description: string; type: AlertType; icon?: Icons } | undefined => {
	if (!place || !menu || !section) return undefined;
	const type = "info";

	// Preorder enabled, place is open, menu is open, section is open, place can take orders,
	// and grouped orders are not enabled. This is a scenario in which no alert should be displayed at all.
	const scenarioOne: boolean =
		place.preOrderEnabled &&
		place.open &&
		menu.open &&
		section.open &&
		place.canTakeOrders &&
		!place.groupedOrders?.enabled;

	// Preorder enabled, place is open, menu is open, section is open, place can NOT take orders.
	const scenarioTwo: boolean =
		place.preOrderEnabled && place.open && menu.open && section.open && !place.canTakeOrders;

	// Preorder enabled, place is open, menu is open, section is NOT open, place can take orders.
	const scenarioThree: boolean =
		place.preOrderEnabled && place.open && menu.open && !section.open && place.canTakeOrders;

	// Preorder enabled, place is open, menu is open, section is NOT open, place can NOT take orders.
	const scenarioFour: boolean =
		place.preOrderEnabled && place.open && menu.open && !section.open && !place.canTakeOrders;

	// Invalid config
	// Preorder enabled, place is open, menu is NOT open, section is open, place can take orders.
	const scenarioFive: boolean =
		place.preOrderEnabled && place.open && !menu.open && section.open && place.canTakeOrders;

	// Invalid config
	// Preorder enabled, place is open, menu is NOT open, section is open, place can NOT take orders.
	const scenarioSix: boolean =
		place.preOrderEnabled && place.open && !menu.open && section.open && !place.canTakeOrders;

	// Preorder enabled, place is open, menu is NOT open, section is NOT open, place can take orders.
	const scenarioSeven: boolean =
		place.preOrderEnabled && place.open && !menu.open && !section.open && place.canTakeOrders;

	// Preorder enabled, place is open, menu is NOT open, section is NOT open, place can NOT take orders.
	const scenarioEight: boolean =
		place.preOrderEnabled && place.open && !menu.open && !section.open && !place.canTakeOrders;

	// Preorder enabled, place is NOT open, menu is open, section is open, place can take orders.
	const scenarioNine: boolean =
		place.preOrderEnabled && !place.open && menu.open && section.open && place.canTakeOrders;

	// Preorder enabled, place is NOT open, menu is open, section is open, place can NOT take orders.
	const scenarioTen: boolean =
		place.preOrderEnabled && !place.open && menu.open && section.open && !place.canTakeOrders;

	// Preorder enabled, place is NOT open, menu is open, section is NOT open, place can take orders.
	const scenarioEleven: boolean =
		place.preOrderEnabled && !place.open && menu.open && !section.open && place.canTakeOrders;

	// Preorder enabled, place is NOT open, menu is open, section is NOT open, place can NOT take orders.
	const scenarioTwelve: boolean =
		place.preOrderEnabled && !place.open && menu.open && !section.open && !place.canTakeOrders;

	// Invalid config
	// Preorder enabled, place is NOT open, menu is NOT open, section is open, place can take orders.
	const scenarioThirteen: boolean =
		place.preOrderEnabled && !place.open && !menu.open && section.open && place.canTakeOrders;

	// Invalid config
	// Preorder enabled, place is NOT open, menu is NOT open, section is open, place can NOT take orders.
	const scenarioFourteen: boolean =
		place.preOrderEnabled && !place.open && !menu.open && section.open && !place.canTakeOrders;

	// Preorder enabled, place is NOT open, menu is NOT open, section is NOT open, place can take orders.
	const scenarioFifteen: boolean =
		place.preOrderEnabled && !place.open && !menu.open && !section.open && place.canTakeOrders;

	// Preorder enabled, place is NOT open, menu is NOT open, section is NOT open, place can NOT take orders.
	const scenarioSixteen: boolean =
		place.preOrderEnabled && !place.open && !menu.open && !section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is open, menu is open, section is open, place can take orders,
	// and grouped orders are not enabled. This is a scenario in which no alert should be displayed at all.
	const scenarioSeventeen: boolean =
		!place.preOrderEnabled &&
		place.open &&
		menu.open &&
		section.open &&
		place.canTakeOrders &&
		!place.groupedOrders?.enabled;

	// Preorder NOT enabled, place is open, menu is open, section is open, place can NOT take orders.
	const scenarioEighteen: boolean =
		!place.preOrderEnabled && place.open && menu.open && section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is open, menu is open, section is NOT open, place can take orders.
	const scenarioNineteen: boolean =
		!place.preOrderEnabled && place.open && menu.open && !section.open && place.canTakeOrders;

	// Preorder NOT enabled, place is open, menu is open, section is NOT open, place can NOT take orders.
	const scenarioTwenty: boolean =
		!place.preOrderEnabled && place.open && menu.open && !section.open && !place.canTakeOrders;

	// Invalid config
	// Preorder NOT enabled, place is open, menu is NOT open, section is open, place can take orders.
	const scenarioTwentyOne: boolean =
		!place.preOrderEnabled && place.open && !menu.open && section.open && place.canTakeOrders;

	// Invalid config
	// Preorder NOT enabled, place is open, menu is NOT open, section is open, place can NOT take orders.
	const scenarioTwentyTwo: boolean =
		!place.preOrderEnabled && place.open && !menu.open && section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is open, menu is NOT open, section is NOT open, place can take orders.
	const scenarioTwentyThree: boolean =
		!place.preOrderEnabled && place.open && !menu.open && !section.open && place.canTakeOrders;

	// Preorder NOT enabled, place is open, menu is NOT open, section is NOT open, place can NOT take orders.
	const scenarioTwentyFour: boolean =
		!place.preOrderEnabled && place.open && !menu.open && !section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is open, section is open, place can take orders.
	const scenarioTwentyFive: boolean =
		!place.preOrderEnabled && !place.open && menu.open && section.open && place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is open, section is open, place can NOT take orders.
	const scenarioTwentySix: boolean =
		!place.preOrderEnabled && !place.open && menu.open && section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is open, section is NOT open, place can take orders.
	const scenarioTwentySeven: boolean =
		!place.preOrderEnabled && !place.open && menu.open && !section.open && place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is open, section is NOT open, place can NOT take orders.
	const scenarioTwentyEight: boolean =
		!place.preOrderEnabled && !place.open && menu.open && !section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is NOT open, section is open, place can take orders.
	const scenarioTwentyNine: boolean =
		!place.preOrderEnabled && !place.open && !menu.open && section.open && place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is NOT open, section is open, place can NOT take orders.
	const scenarioThirty: boolean =
		!place.preOrderEnabled && !place.open && !menu.open && section.open && !place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open, menu is NOT open, section is NOT open, place can take orders.
	const scenarioThirtyOne: boolean =
		!place.preOrderEnabled && !place.open && !menu.open && !section.open && place.canTakeOrders;

	// Preorder NOT enabled, place is NOT open,
	// menu is NOT open, section is NOT open, place can NOT take orders.
	const scenarioThirtyTwo: boolean =
		!place.preOrderEnabled && !place.open && !menu.open && !section.open && !place.canTakeOrders;

	// List of opening hours for menu and place are empty
	const scenarioThirtyThree: boolean = !place.openingHours.length && !menu.openingHours.length;

	// Grouped orders enabled and has a time limit,
	// and the delivery option is "EAT_AT_RESTAURANT".
	// IMPORTANT that this is the last case as all other cases are more important.
	const scenarioThirtyFour: boolean =
		place.open &&
		menu.open &&
		section.open &&
		place.canTakeOrders &&
		place.groupedOrders?.enabled === true &&
		(place.groupedOrders?.minutes ?? 0) > 0 &&
		deliveryOptionType === "EAT_AT_RESTAURANT";

	switch (true) {
		// The only scenario that checks for empty lists of opening hours,
		// added late but needs to go first
		case scenarioThirtyThree:
			logDebug(
				LogPrefix.Menu,
				`Found no opening hours to display (Place: ${place.name}, Menu: ${menu.displayName})`,
			);
			return {
				description: i18next.t("location_closed_menu_readonly"),
				type,
			};
		case scenarioOne:
			return undefined;
		case scenarioTwo:
			return {
				description: `${i18next.t("location_temporarily_closed_pre_order_only")}.`,
				type,
			};
		case scenarioThree:
		case scenarioFour: {
			const openAt = createCustomOpeningHoursLabel(section.openingStatus);
			return {
				description: `${i18next.t("menu_section_only_pre_order_opens_at_x", {
					0: openAt,
				})}.`,
				type,
			};
		}
		case scenarioFive:
		case scenarioSix:
		case scenarioSeven:
		case scenarioEight:
		case scenarioNine:
		case scenarioTen:
		case scenarioEleven:
		case scenarioTwelve: {
			const openAt = createCustomOpeningHoursLabel(section.openingStatus);
			return {
				description: `${i18next.t("menu_only_pre_order_opens_at_x", {
					0: openAt,
				})}.`,
				type,
			};
		}
		case scenarioThirteen:
		case scenarioFourteen:
		case scenarioFifteen:
		case scenarioSixteen: {
			const openAt = createCustomOpeningHoursLabel(section.openingStatus);
			return {
				description: `${i18next.t("menu_only_pre_order_opens_at_x", {
					0: openAt,
				})}.`,
				type,
			};
		}
		case scenarioSeventeen: {
			return undefined;
		}
		case scenarioEighteen: {
			return {
				description: `${i18next.t("location_temporarily_closed")}.`,
				type,
			};
		}
		case scenarioNineteen:
		case scenarioTwenty: {
			const openAt = createCustomOpeningHoursLabel(section.openingStatus);
			return {
				description: `${i18next.t("menu_section_closed_opens_at_x", {
					0: openAt,
				})}.`,
				type,
			};
		}
		case scenarioTwentyOne:
		case scenarioTwentyTwo:
		case scenarioTwentyThree:
		case scenarioTwentyFour: {
			const openAt = createCustomOpeningHoursLabel(section.openingStatus);
			return {
				description: i18next.t("menu_closed_opens_at_x", {
					0: openAt,
				}),
				type,
			};
		}
		case scenarioTwentyFive:
		case scenarioTwentySix:
		case scenarioTwentySeven:
		case scenarioTwentyEight:
		case scenarioTwentyNine:
		case scenarioThirty:
		case scenarioThirtyOne:
		case scenarioThirtyTwo: {
			const openAt = createCustomOpeningHoursLabel(place.openingStatus);
			return {
				description: i18next.t("location_opens_at_x_only_readable", {
					0: openAt,
				}),
				type,
			};
		}
		case scenarioThirtyFour: {
			return {
				description: i18next.t("order_within_x_minutes_to_get_them_together", {
					0: place.groupedOrders?.minutes,
				}),
				type: "neutral",
				icon: Icons.Users,
			};
		}
		default:
			return undefined;
	}
};
